import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
import { saveFlashcardDecks } from "./resources/flashcard.js";
import { saveQuizzes } from "./resources/quiz.js";
import { saveArticles, saveMyArticles } from "./resources/article.js";
import { saveBookmarks } from "./resources/bookmark.js";
import { saveTocs } from "./resources/toc.js";

/**
 * Save offline resources (schools, classrooms, resource assignments)
 * @param {EduQuizDB} db - Database instance
 * @param {Object} offlineData - Offline data to save
 * @returns {Promise<boolean>}
 */
export async function saveOfflineResources(db, offlineData) {
    try {
        console.log("Saving offline resources:", offlineData);

        // Clear existing data
        await db.schools.clear();
        await db.classrooms.clear();
        await db.resourceAssignments.clear();
        await db.flashcardDecks.clear();
        await db.flashcardsCards.clear();
        await db.quizzes.clear();
        await db.quizQuestions.clear();
        await db.quizQuestionOptions.clear();
        await db.bookmarks.clear();
        await db.articles.clear();
        await db.articleResources.clear();
        await db.myResources.clear();
        await db.tableOfContents.clear();
        await db.tocThemes.clear();
        await db.tocParts.clear();
        await db.tocChapters.clear();
        await db.tocChapterResources.clear();

        // Mark all existing assets for deletion instead of clearing
        await db.markAllAssetsForDeletion();

        // Save schools
        if (offlineData.schools && offlineData.schools.length > 0) {
            const schoolsData = offlineData.schools.map((school) => ({
                serverId: school.id,
                name: school.name,
                address: school.address || null,
                updatedAt: school.updated_at || new Date().toISOString(),
            }));
            console.log("Saving schools:", schoolsData);
            await db.schools.bulkAdd(schoolsData);
        }

        // Save classrooms
        const classroomsData = [];
        offlineData.schools?.forEach((school) => {
            school.classrooms?.forEach((classroom) => {
                classroomsData.push({
                    serverId: classroom.id,
                    schoolId: school.id,
                    name: classroom.name,
                    description: classroom.description || null,
                    updatedAt:
                        classroom.updated_at || new Date().toISOString(),
                });
            });
        });
        if (classroomsData.length > 0) {
            console.log("Saving classrooms:", classroomsData);
            await db.classrooms.bulkAdd(classroomsData);
        }

        // Save resource assignments (now just references)
        if (
            offlineData.resource_assignments &&
            offlineData.resource_assignments.length > 0
        ) {
            const assignmentsData = offlineData.resource_assignments.map(
                (assignment) => ({
                    serverId: assignment.id,
                    classroomId: assignment.classroom_id,
                    resourceType: assignment.resource_type, // Will be populated from resources array
                    resourceId: assignment.resource_id, // Will be populated from resources array
                    assignedAt:
                        assignment.assigned_at || new Date().toISOString(),
                }),
            );

            console.log("Saving resource assignments:", assignmentsData);
            await db.resourceAssignments.bulkAdd(assignmentsData);
        }

        // Save resources from the root-level resources array
        offlineData.resources = (offlineData.resources || []).filter(Boolean);
        if (offlineData.resources.length > 0) {
            await saveFlashcardDecks(
                db,
                offlineData.resources,
                RESOURCE_TYPES.FLASHCARD_DECK
            );
            await saveQuizzes(db, offlineData.resources, RESOURCE_TYPES.QUIZ);
            await saveBookmarks(
                db,
                offlineData.resources,
                RESOURCE_TYPES.BOOKMARK
            );
            await saveArticles(
                db,
                offlineData.resources,
                RESOURCE_TYPES.ARTICLE
            );
            await saveTocs(db, offlineData.resources);
        }

        // Handle my_resources (user's own resources)
        if (
            offlineData.my_resources &&
            offlineData.my_resources.length > 0
        ) {
            console.log(
                "Processing my_resources:",
                offlineData.my_resources,
            );

            // Save my_resources to the myResources table
            const myResourcesData = offlineData.my_resources.map(
                (resource) => ({
                    resourceId: resource.id,
                    resourceType: resource.resource_type,
                }),
            );

            if (myResourcesData.length > 0) {
                console.log("Saving my resources:", myResourcesData);
                await db.myResources.bulkAdd(myResourcesData);
            }

            // Process articles from my_resources
            await saveMyArticles(
                db,
                offlineData.my_resources,
                RESOURCE_TYPES.ARTICLE
            );
        }

        // Save assets from root-level assets array (images and audios)
        if (offlineData.assets && offlineData.assets.length > 0) {
            console.log("Saving assets:", offlineData.assets.length);

            // Save or update each asset with the new shouldDelete flag and urlUpdatedAt
            for (const asset of offlineData.assets) {
                const cacheFolder = asset.type === 'audio' ? 'cached-audios' : 'cached-images';
                const urlCachePWA = asset.extension
                    ? `/app/${cacheFolder}/${asset.uuid}${asset.extension}`
                    : null;

                await db.saveAsset(
                    asset.uuid,
                    asset.url,
                    asset.extension || '',
                    asset.type || 'image',
                    urlCachePWA
                );
            }

            console.log("Assets saved successfully:", offlineData.assets.length);
        }

        console.log("Offline resources saved successfully");
        return true;
    } catch (error) {
        console.error("Failed to save offline resources:", error);
        throw error;
    }
}
